import React, { ReactNode, useEffect } from 'react';
import { useRootSelector } from 'common/features/featuresReducer';
import { useDispatch } from 'react-redux';
import {
    getOptionsThunk,
    resetOrganizationOptions,
} from 'common/features/store/duck/organization/duck';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Value } from 'firebase/remote-config';
import { UserAuthState, selectUserAuthState } from 'common/features/store/duck/account/duck';
import { LoadingScreen } from 'components/loading-screen/LoadingScreen';
import { useFavorites } from 'common/hooks/useFavorites';
import { fetchFeatureFlagsV2, selectHasFetchedFeatureFlags } from 'common/features/store/duck/ffs';
import { useInitRecentSearchHistory } from '../../features/search/components/utils';
import { CONSTRUCTOR_KEY, IS_FLIPGIVE } from 'common/env';

const BaseAppInit = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch<any>();
    const userId = useRootSelector((r) => r.store.home.homeDetails?.profile?.userId);
    const userAuthState = useRootSelector(selectUserAuthState);
    const optionsLastUpdated = useRootSelector(
        (s) => s.store.organization.optionsAsyncState.lastUpdate
    );

    const homeDetailsLastUpdated = useRootSelector(
        (s) => s.store.home.homeDetailsAsyncState.lastUpdate
    );
    const hasFetchedFeaturedFlags = useRootSelector(selectHasFetchedFeatureFlags);

    const isShipToCoordinatorFeatureEnabled = useFirebaseConfigValues(
        'enable_ship_to_coordinator',
        true,
        (v: Value) => v.asBoolean()
    );
    useInitRecentSearchHistory();

    useEffect(() => {
        if (IS_FLIPGIVE && CONSTRUCTOR_KEY) {
            (window as any).cnstrc = (window as any).cnstrc || {};
            (window as any).cnstrc.indexKey = CONSTRUCTOR_KEY;
        }
    }, []);

    useEffect(() => {
        if (userId) {
            // NOTE: We need to expose the userId to constructor
            (window as any).userId = userId;

            if (isShipToCoordinatorFeatureEnabled) {
                dispatch(getOptionsThunk());
            } else {
                dispatch(resetOrganizationOptions());
            }

            dispatch(fetchFeatureFlagsV2());
        }
    }, [userId, isShipToCoordinatorFeatureEnabled, dispatch]);

    const hasFetchedOrgOptions = optionsLastUpdated > 0;
    const hasFetchedHomeDetails = homeDetailsLastUpdated > 0;

    if (
        userAuthState === UserAuthState.NOTAUTHENTICATED ||
        (userAuthState === UserAuthState.AUTHENTICATED &&
            hasFetchedOrgOptions &&
            hasFetchedHomeDetails &&
            hasFetchedFeaturedFlags)
    ) {
        return <>{children}</>;
    }

    return <LoadingScreen />;
};

export const AppInit = ({ children }: { children: ReactNode }) => {
    useFavorites();
    return <BaseAppInit>{children}</BaseAppInit>;
};

export const PaymentAppInit = ({ children }: { children: ReactNode }) => {
    return <BaseAppInit>{children}</BaseAppInit>;
};
